import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as _moment from 'moment';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { AccordionSection } from 'src/app/models/accordion-section';
import { CampaignStatus } from 'src/app/models/campaign';
import { ChannelStatus, approvedChannelStatuses } from 'src/app/models/channel';
import { MatDialog } from '@angular/material/dialog';
import { LoadingOverlayService } from 'src/app/shared/loading-overlay/loading-overlay.service';
import {
    getChannelSupplierStatus,
    shouldShowExitConfirmationModal,
    showChannelStatusDistinction,
    checkIfModeExistsInQueryParams
} from 'src/app/shared/utility/campaign';
import { CampaignService } from '../../../services/campaign.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { CanComponentDeactivate } from '../guards/can-deactivate.interface';
import { AppConfig } from 'src/app/core/configs/app.config';
import { AttentionModalComponent } from 'src/app/shared/attention-modal/attention-modal.component';
const moment = _moment;

@Component({
    selector: 'app-product-schedule',
    templateUrl: './product-schedule.component.html',
    styleUrls: ['./product-schedule.component.scss']
})
export class ProductScheduleComponent implements OnInit, OnDestroy, AfterViewChecked, CanComponentDeactivate {
    public form: FormGroup;
    // Coalesce rapid schedule edits (matrix drags, multi-select) into one save.
    private readonly AUTO_SAVE_DEBOUNCE_MS = 600;
    private pendingSaveTimer: any = null;
    campaignId;
    campaign;
    CampaignStatus = CampaignStatus;
    ChannelStatus = ChannelStatus;
    loading = false;
    previewMode = false;
    partialEditMode = false;
    liveEditMode = false;
    shouldDisableAllPointers = false;
    isPublish = false;
    selectedChannels = new Set();
    approvedProductDates = [];
    allAvailableChannels = [];
    channelsImpressions = {};
    sections: AccordionSection[] = [
        {
            title: 'Your approved channels',
            content: { formName: 'approvedSchedules' },
            open: false,
            previewMode: true
        },
        {
            title: 'Select your schedule for the rejected and pending channels',
            content: { formName: 'schedules' },
            open: true,
            previewMode: false
        }
    ];

    minStartDate;
    minEndDate;

    public constructor(
        private loadingOverlayService: LoadingOverlayService,
        private formBuilder: FormBuilder,
        private campaignService: CampaignService,
        private authenticationService: AuthenticationService,
        private router: Router,
        public dialog: MatDialog,
        private alertService: AlertService,
        private route: ActivatedRoute
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.previewMode = checkIfModeExistsInQueryParams(this.route, 'preview');
        this.partialEditMode = checkIfModeExistsInQueryParams(this.route, 'partial-edit-mode');
        this.liveEditMode = checkIfModeExistsInQueryParams(this.route, 'live-edit-mode');
        this.shouldDisableAllPointers = [this.liveEditMode, this.previewMode].some(Boolean);
        this.getLocationsAndCampaign();
    }

    showChannelStatusDistinction() {
        return showChannelStatusDistinction(
            this.campaign?.status,
            this.authenticationService.getSessionRole()
        );
    }

    filterAvailableChannelsByStatus(scheduleType) {
        return scheduleType === 'approvedSchedules'
            ? this.allAvailableChannels.filter(channel =>
                approvedChannelStatuses.includes(channel.status)
            )
            : this.allAvailableChannels.filter(
                channel => !approvedChannelStatuses.includes(channel.status)
            );
    }

    ngAfterViewChecked(): void {
        const classNames = ['day-hour', 'eday-hour', 'ehour-day'];
        if (!this.shouldDisableAllPointers && this.showChannelStatusDistinction()) {
            for (let i = 0; i < this.approvedSchedules.length; i++) {
                const container = document.getElementById(`approvedSchedules-${i}`);
                if (!container) continue;
                for (let j = 0; j < classNames.length; j++) {
                    const className = container.getElementsByClassName(classNames[j]);
                    this.disablePointerForPreviewDayHours(className);
                }
            }
        } else {
            for (let j = 0; j < classNames.length; j++) {
                const className = document.getElementsByClassName(classNames[j]);
                this.disablePointerForPreviewDayHours(className);
            }
        }
    }

    canDeactivate(): boolean {
        // Always show exit confirmation modal unless in preview mode
        return this.previewMode;
    }

    private isReadOnlyFlow(): boolean {
        return this.previewMode || !this.authenticationService.isClient();
    }

    private showSaveError(error: any): void {
        this.alertService.error('Error: ' + error, { id: 'save-campaign', autoClose: true });
    }

    async saveDraft(): Promise<boolean> {
        if (this.isReadOnlyFlow()) {
            return true;
        }

        this.alertService.clear();
        if (!this.campaign || !this.campaign._id) {
            this.alertService.error('Campaign not found. Please try again.', {
                id: 'save-campaign',
                autoClose: true
            });
            return false;
        }

        this.cancelPendingSave();
        return new Promise(resolve => {
            this.loadingOverlayService.show();
            this.cleanCampaignProducts();
            this.campaignService.update(this.campaign._id, this.campaign).subscribe({
                next: data => {
                    this.loadingOverlayService.hide();
                    this.campaign = data;
                    this.alertService.success('Campaign schedule saved successfully.', {
                        id: 'save-campaign',
                        autoClose: true
                    });
                    resolve(true);
                },
                error: error => {
                    this.showSaveError(error);
                    this.loadingOverlayService.hide();
                    resolve(false);
                }
            });
        });
    }

    handleSelectionRemoval(index) {
        const schedules = this.form.get('schedules') as FormArray;
        const channelsControl = schedules.at(index).get('channels') as FormControl;

        if (channelsControl.value.length != 0) {
            const dialogRef = this.dialog.open(AttentionModalComponent, {
                width: AppConfig.dialog_width_xs,
                maxHeight: AppConfig.dialog_max_height,
                data: {
                    description: `Are you sure you want to remove the selected slots?`
                }
            });

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    this.removeSchedule(index);
                }
            });
        } else {
            this.removeSchedule(index);
        }
    }

    disablePointerForPreviewDayHours(dayHours: HTMLCollectionOf<Element>) {
        if (this.shouldDisableAllPointers) {
            for (let i = 0; i < dayHours.length; i++) {
                const element = dayHours[i] as HTMLElement;
                element.style.pointerEvents = 'none';
            }
        }
    }

    toggleSection(index: number) {
        this.sections[index].open = !this.sections[index].open;
    }

    preventInvalidKeys(event: KeyboardEvent) {
        const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete', '/'];
        if (isNaN(Number(event.key)) && !allowedKeys.includes(event.key)) {
            event.preventDefault();
        }
    }

    get schedules(): FormArray {
        return this.form.get('schedules') as FormArray;
    }

    get approvedSchedules(): FormArray {
        return this.form.get('approvedSchedules') as FormArray;
    }

    createChannelsByCountryStructure() {
        // channelsDetails already contains exactly the campaign's channels (+ removed-product
        // channels), so there's no catalog to filter — just dedupe and stamp the supplier status.
        (this.campaign.channelsDetails || []).forEach(channel => {
            if (this.allAvailableChannels.some(ch => ch.idChannel == channel.idChannel)) {
                return;
            }
            channel.status = getChannelSupplierStatus(this.campaign, channel.idChannel);
            this.allAvailableChannels.push(channel);
        });
    }

    addFormValuesToTheAccordionContent() {
        this.sections.forEach(section => {
            if (section.content.formName === 'approvedSchedules') {
                section.content.formValue = this.approvedSchedules;
            } else {
                section.content.formValue = this.schedules;
            }
        });
    }

    shouldScheduleAppear(schedule, formName, i) {
        const scheduleEndDate = schedule.get('end_date').value;
        const scheduleStartDate = schedule.get('start_date').value;
        const scheduleDatesNull = scheduleStartDate == null || scheduleEndDate == null;

        if (scheduleStartDate == null || scheduleEndDate == null) {
            return false;
        }

        const validEndDate = new Date(scheduleEndDate) >= this.minStartDate?.setHours(0, 0, 0, 0);
        const validStartDate =
            new Date(scheduleStartDate) >= this.minStartDate?.setHours(0, 0, 0, 0);

        if (!this.isScheduleDatesChannelsDirtied(schedule)) {
            return true;
        }

        if (!validStartDate || !validEndDate) {
            this.handleStartDateEndDateErrors(schedule, validEndDate);
            return false;
        }

        return this.form.get([formName, i, 'channels']).value?.length !== 0;
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            approvedSchedules: this.formBuilder.array([this.createSchedule()]),
            schedules: this.formBuilder.array([this.createSchedule()])
        });
    }

    public addSchedule(): void {
        const schedules = this.form.get('schedules') as FormArray;
        schedules.push(this.createSchedule());
    }

    private createSchedule(): FormGroup {
        return this.formBuilder.group(
            {
                start_date: this.formBuilder.control(null, Validators.required),
                end_date: this.formBuilder.control(null, Validators.required),
                min_end_date: this.formBuilder.control(null, Validators.required),
                channels: this.formBuilder.control([]),
                matrix: this.formBuilder.control(null),
                datesDirective: this.formBuilder.control(null),
                productMatrix: this.formBuilder.control(null)
            },
            { validator: this.startDateGreaterThanEndDate }
        );
    }

    public removeSchedule(index: number): void {
        const schedules = this.form.get('schedules') as FormArray;
        if (schedules.length > 1) {
            schedules.removeAt(index);
        }
        this.updateCampaign();
    }

    getScheduleChannels(channelIndex: number, scheduleType = 'schedules'): any[] {
        const channelsControl =
            scheduleType === 'approvedSchedules'
                ? (this.approvedSchedules.at(channelIndex).get('channels') as FormControl)
                : (this.schedules.at(channelIndex).get('channels') as FormControl);
        return channelsControl ? channelsControl.value : [];
    }

    public getDisabledChannelIds(
        scheduleIndex: number,
        scheduleType = 'schedules',
        sectionPreviewMode = false
    ): number[] {
        if (this.shouldDisableAllPointers || sectionPreviewMode) {
            return this.filterAvailableChannelsByStatus(scheduleType).map(
                (channel: any) => channel.idChannel
            );
        }

        const schedules = this.form.get(scheduleType) as FormArray;
        const currentScheduleChannels = schedules.at(scheduleIndex).get('channels')?.value || [];
        const currentScheduleChannelIds = new Set(
            currentScheduleChannels.map((ch: any) => ch.idChannel)
        );

        const disabledIds: number[] = [];

        schedules.controls.forEach((schedule: FormGroup, index: number) => {
            if (index !== scheduleIndex) {
                const channels = schedule.get('channels')?.value || [];
                channels.forEach((channel: any) => {
                    if (!currentScheduleChannelIds.has(channel.idChannel)) {
                        disabledIds.push(channel.idChannel);
                    }
                });
            }
        });

        return disabledIds;
    }

    private fromToDatesExist(scheduleIndex): boolean {
        return (
            this.form.value.schedules[scheduleIndex].end_date != null &&
            this.form.value.schedules[scheduleIndex].start_date != null
        );
    }

    public toggleAllSelection(schedule, index, scheduleType = 'schedules') {
        if (scheduleType != 'schedules') {
            return;
        }
        const schedules = this.form.get('schedules') as FormArray;
        const channelsControl = schedules.at(index).get('channels') as FormControl;

        if (!this.areAllChannelsSelected()) {
            const disabledChannelIds = this.getDisabledChannelIds(index, scheduleType);
            channelsControl.setValue(
                this.filterAvailableChannelsByStatus(scheduleType).filter(
                    channel => !disabledChannelIds.includes(channel.idChannel)
                )
            );
        } else {
            this.clearScheduleFormFields(index);
            channelsControl.setValue([]);
            this.updateCampaign();
        }
    }

    public onScheduleChannelsChanged(index: number, event: any): void {
        const selectedChannels = event.filter(e => e != 'All');
        const schedules = this.form.get('schedules') as FormArray;
        const channelsControl = schedules.at(index).get('channels') as FormControl;

        if (selectedChannels) {
            channelsControl.setValue(selectedChannels);

            if (this.areAllChannelsSelected()) {
                schedules.controls = schedules.controls.filter((ctrl: AbstractControl) => {
                    const channelsControl = (ctrl as FormGroup).get('channels') as FormControl;
                    return channelsControl.value && channelsControl.value.length > 0;
                });
            }
            this.updateCampaign();
        }
    }

    public removeScheduleChannel(scheduleIndex: number, channelIndex: number): void {
        const channelsControl = this.form.get([
            'schedules',
            scheduleIndex,
            'channels'
        ]) as FormControl;

        const currentChannels = channelsControl.value as any[];
        currentChannels.splice(channelIndex, 1);

        channelsControl.setValue([...currentChannels]);

        let selectedChannel;
        let convertedSelectedChannels = Array.from(this.selectedChannels);

        for (let i = 0; i < convertedSelectedChannels.length; i++) {
            if (i == channelIndex) {
                selectedChannel = convertedSelectedChannels[i];
            }
        }

        if (currentChannels.length === 0) {
            this.clearScheduleFormFields(scheduleIndex);
        }

        this.updateCampaign();
    }

    private clearScheduleFormFields(scheduleIndex: number): void {
        this.form.get(['schedules', scheduleIndex, 'productMatrix']).setValue(null);
        this.form.get(['schedules', scheduleIndex, 'matrix']).setValue(null);

        // Clear date fields when no channels remain
        this.form.get(['schedules', scheduleIndex, 'start_date']).setValue(null);
        this.form.get(['schedules', scheduleIndex, 'min_end_date']).setValue(null);
        this.form.get(['schedules', scheduleIndex, 'end_date']).setValue(null);
        this.form.get(['schedules', scheduleIndex, 'datesDirective']).setValue(null);

        // Clear validation errors for the date fields
        const schedule = this.schedules.at(scheduleIndex) as FormGroup;
        schedule.get('start_date').setErrors(null);
        schedule.get('end_date').setErrors(null);
        schedule.setErrors(null);
    }

    public areAllChannelsSelected(): boolean {
        const schedules = this.form.get('schedules') as FormArray;
        const pickedChannels = schedules.controls.reduce((acc: any[], schedule: FormGroup) => {
            const channels = schedule.get('channels') as FormControl;
            return acc.concat(channels.value);
        }, []);

        return this.filterAvailableChannelsByStatus('schedules').every((availableChannel: any) =>
            pickedChannels.some(
                (selectedChannel: any) => selectedChannel.idChannel === availableChannel.idChannel
            )
        );
    }

    public getScheduleDatesDirective(scheduleIndex: number, scheduleType = 'schedules') {
        return this.form.get([scheduleType, scheduleIndex, 'datesDirective']).value;
    }

    convertScheduleToTimingMatrix(schedule) {
        let matrix = [];

        let startDateAux = moment(schedule.startDate).subtract(1, 'day');
        while (startDateAux.isBefore(moment(schedule.endDate))) {
            let slotHex = '';
            startDateAux.add(1, 'day');
            for (let i = 0; i < schedule.dayPartings.length; i++) {
                if (schedule.dayPartings[i].date == startDateAux.format('YYYY-MM-DD')) {
                    slotHex = schedule.dayPartings[i].slotHex;
                    break;
                }
            }
            let array = this.getBitsArrayFromHex(slotHex);
            matrix.push(array);
        }

        return matrix;
    }

    setDatesForCampaignStatus() {
        const schedules = this.form.get('schedules') as FormArray;

        schedules.controls.forEach((schedule: FormGroup) => {
            this.minStartDate = moment().add(2, 'd').toDate();
            this.minEndDate = moment().add(2, 'd').toDate();
        });
    }

    determineScheduleMinEndDate(schedule) {
        const scheduleMinEndDate = schedule.get('min_end_date');
        const scheduleMinEndDateExists = scheduleMinEndDate?.value != null;
        const isGreaterThanMinEndDate = new Date(scheduleMinEndDate?.value) > this.minEndDate;

        return scheduleMinEndDateExists && isGreaterThanMinEndDate
            ? scheduleMinEndDate?.value
            : this.minEndDate;
    }

    disableDateFieldsForPreviewMode() {
        this.schedules.controls.forEach((schedule: FormGroup) => {
            if (this.shouldDisableAllPointers) {
                schedule.get('start_date').disable();
                schedule.get('end_date').disable();
            } else {
                schedule.get('start_date').enable();
                schedule.get('end_date').enable();
            }
        });

        this.approvedSchedules.controls.forEach((approvedSchedule: FormGroup) => {
            approvedSchedule.get('start_date').disable();
            approvedSchedule.get('end_date').disable();
        });
    }

    startDateGreaterThanEndDate(formGroup: FormGroup, responseAsForm) {
        let startDateMoment =
            formGroup.get('start_date').value instanceof moment
                ? formGroup.get('start_date').value
                : moment(formGroup.get('start_date').value);

        let endDateMoment =
            formGroup.get('end_date').value instanceof moment
                ? formGroup.get('end_date').value
                : moment(formGroup.get('end_date').value);

        let result;
        if (responseAsForm) {
            result = startDateMoment.isAfter(endDateMoment);
        } else {
            result = startDateMoment.isAfter(endDateMoment) ? { unsequencial: true } : null;
        }
        return result;
    }

    areDatesEqualIgnoringTime(date1, date2) {
        const d1 = date1 instanceof Date ? date1 : new Date(date1);
        const d2 = date2 instanceof Date ? date2 : new Date(date2);

        return (
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate()
        );
    }

    onTimingChanged(scheduleIndex: number, $event, approvedSchedules = false) {
        const previousState = this.campaign?.previousCampaignState;
        const canEditApproved =
            this.campaign?.status === 'BEING_EDITED' &&
            previousState?.status === 'PAUSED' &&
            ['APPROVED', 'PARTIALLY_APPROVED'].includes(previousState?.relaunchStatus);

        if (this.shouldDisableAllPointers || (approvedSchedules && !canEditApproved)) {
            return;
        }

        const schedules = approvedSchedules ? this.approvedSchedules : this.schedules;
        const schedule = schedules.at(scheduleIndex);

        const minThreshold = new Date(this.minStartDate || new Date()).setHours(0, 0, 0, 0);
        const startDate = new Date(schedule.get('start_date').value).getTime();
        const endDate = new Date(schedule.get('end_date').value).getTime();

        const isValidStartDate = !isNaN(startDate) && startDate >= minThreshold;
        const isValidEndDate = !isNaN(endDate) && endDate >= minThreshold;

        if (isValidStartDate) {
            schedule.get('productMatrix').setValue($event);
            this.updateCampaign();
        } else {
            this.handleStartDateEndDateErrors(schedule, isValidEndDate);
        }

    }

    getBitsArrayFromHex(slotHex) {
        let binary = parseInt(slotHex, 16).toString(2);
        let binaryCut = binary.substring(4, binary.length);
        let arraySpit = binaryCut.split('');
        let array = [];
        for (var j = 0; j < arraySpit.length; j++) {
            array.push(Number(arraySpit[j]));
        }
        return array;
    }

    getLocationsAndCampaign() {
        this.loadingOverlayService.show();
        this.campaignId = this.route.snapshot.paramMap.get('id');
        if (this.campaignId == null) {
            this.addSchedule();
            return;
        }
        // Channel detail rides along on the campaign response (campaign.channelsDetails),
        // so Step 3 no longer fetches the full available-channels catalog.
        this.campaignService.getById(this.campaignId, true).subscribe(campaignRes => {
            this.loadingOverlayService.hide();
            this.campaign = campaignRes;

            const removedChannels: number[] = [].concat(
                ...(campaignRes.removedProducts || []).map(p => p.channels || [])
            );
            this.selectedChannels = new Set([
                ...(campaignRes.channels || []),
                ...removedChannels
            ]);

            this.createChannelsByCountryStructure();

            this.setSchedules();
            this.setDatesForCampaignStatus();
            this.disableDateFieldsForPreviewMode();
            this.setDatesForCampaignStatus();
            if (this.campaign.products && this.campaign.products.length > 0) {
                this.form.markAllAsTouched();
                this.schedules.controls.forEach(control => {
                    const group = control as FormGroup;
                    this.shouldScheduleAppear(group, 'schedules', this.schedules.controls.indexOf(control));
                    group.controls['start_date'].updateValueAndValidity();
                    group.controls['end_date'].updateValueAndValidity();
                });
            }
        });
    }

    updateCampaign() {
        if (this.isReadOnlyFlow()) {
            return;
        }
        // Debounce: a burst of edits results in a single PUT (and a single highlights re-fetch).
        if (this.pendingSaveTimer) {
            clearTimeout(this.pendingSaveTimer);
        }
        this.pendingSaveTimer = setTimeout(() => {
            this.pendingSaveTimer = null;
            this.persistCampaignChanges();
        }, this.AUTO_SAVE_DEBOUNCE_MS);
    }

    private persistCampaignChanges() {
        this.alertService.clear();
        this.cleanCampaignProducts();
        this.campaignService.update(this.campaign._id, this.campaign).subscribe({
            next: data => {
                this.campaign = data;
            },
            error: error => this.showSaveError(error)
        });
    }

    /** Cancels a queued auto-save so an explicit save/proceed isn't followed by a stale PUT. */
    private cancelPendingSave() {
        if (this.pendingSaveTimer) {
            clearTimeout(this.pendingSaveTimer);
            this.pendingSaveTimer = null;
        }
    }

    ngOnDestroy(): void {
        this.cancelPendingSave();
    }

    cleanCampaignProducts() {
        let cleanedProducts = [];

        if (this.showChannelStatusDistinction()) {
            for (let index = 0; index < this.approvedSchedules.value.length; index++) {
                const convertedScheduleToProduct = this.convertScheduleToProduct(
                    this.approvedSchedules.value[index],
                    index
                );

                if (convertedScheduleToProduct) cleanedProducts.push(convertedScheduleToProduct);
            }
        }

        for (let index = 0; index < this.schedules.value.length; index++) {
            const convertedScheduleToProduct = this.convertScheduleToProduct(
                this.schedules.value[index],
                cleanedProducts.length
            );

            if (convertedScheduleToProduct) cleanedProducts.push(convertedScheduleToProduct);
        }

        this.campaign.products = cleanedProducts.length == 0 ? null : [...cleanedProducts];
    }

    handleStartDateEndDateErrors(schedule, validEndDate) {
        const errors: { oldStartDate?: boolean; oldEndDate?: boolean } = {
            oldStartDate: true
        };

        if (!validEndDate) {
            errors.oldEndDate = true;
        }

        schedule.setErrors(errors);
    }

    includesStartDateErrors(schedule: FormGroup) {
        const scheduleControl = schedule.get('start_date');
        return scheduleControl.value && schedule.hasError('oldStartDate');
    }

    includesEndDateErrors(schedule: FormGroup) {
        const scheduleControl = schedule.get('end_date');
        return scheduleControl.value && (schedule.hasError('oldEndDate') || schedule.hasError('unsequencial'));
    }

    private step4Url(id: string): string {
        return '/campaign/step-4/' + id + (this.partialEditMode ? '?partial-edit-mode=true' : '');
    }

    navigateNextStep() {
        if (this.isReadOnlyFlow()) {
            this.router.navigateByUrl(this.step4Url(this.campaign._id));
            return;
        }

        this.cancelPendingSave();
        this.alertService.clear();
        this.loadingOverlayService.show();
        this.cleanCampaignProducts();

        this.campaignService.update(this.campaign._id, this.campaign).subscribe({
            next: data => {
                this.loadingOverlayService.hide();
                this.router.navigateByUrl(this.step4Url(data._id));
            },
            error: error => {
                this.showSaveError(error);
                this.loadingOverlayService.hide();
            }
        });
    }

    setSchedule(product, index, scheduleType = 'schedules') {
        const schedules = this.form.get(scheduleType) as FormArray;
        schedules.push(
            this.formBuilder.group(
                {
                    start_date: this.formBuilder.control(product.startDate, Validators.required),
                    end_date: this.formBuilder.control(product.endDate, Validators.required),
                    min_end_date: this.formBuilder.control(product.startDate, Validators.required),
                    channels: this.formBuilder.control(
                        this.allAvailableChannels.filter(channel =>
                            product.channels.includes(channel.idChannel)
                        )
                    ),
                    datesDirective: this.formBuilder.control({
                        start_date: new Date(product.startDate),
                        end_date: new Date(product.endDate)
                    }),
                    matrix: this.formBuilder.control(null),
                    productMatrix: this.formBuilder.control(null)
                },
                { validator: this.startDateGreaterThanEndDate }
            )
        );

        const matrix = schedules.get([index, 'matrix']);
        const productMatrix = schedules.get([index, 'productMatrix']);
        matrix.patchValue([...this.convertScheduleToTimingMatrix(product)]);
        productMatrix.patchValue([...this.convertScheduleToTimingMatrix(product)]);

        this.onDateChanged(index, false, scheduleType);

        matrix.patchValue([...this.convertScheduleToTimingMatrix(product)]);
        productMatrix.patchValue([...this.convertScheduleToTimingMatrix(product)]);
    }

    setSchedules() {
        if (this.campaign && this.campaign.products && this.campaign.products.length > 0) {
            this.schedules.clear();
            this.approvedSchedules.clear();
            this.campaign.products = this.campaign.products.filter(product => {
                return product.channels.some(element => this.selectedChannels.has(element));
            });

            if (this.campaign.products.length == 0) this.addSchedule();
            else {
                let resultProducts = [];
                let productChannels = [];
                let approvedIdx = 0;
                let otherIdx = 0;
                this.campaign.products.forEach((product, index) => {
                    if (this.showChannelStatusDistinction()) {
                        productChannels.push(...product.channels);

                        const approvedProduct = this.getProductByChannelStatus(product, 'APPROVED');
                        const otherProduct = this.getProductByChannelStatus(product, 'OTHER');

                        if (approvedProduct !== null) {
                            this.setSchedule(approvedProduct, approvedIdx, 'approvedSchedules');
                            resultProducts.push(approvedProduct);
                            this.approvedProductDates.push({
                                startDate: approvedProduct.startDate,
                                endDate: approvedProduct.endDate
                            });
                            approvedIdx++;
                        }

                        if (otherProduct !== null) {
                            this.setSchedule(otherProduct, otherIdx, 'schedules');
                            resultProducts.push(otherProduct);
                            otherIdx++;
                        }
                    } else {
                        this.setSchedule(product, index, 'schedules');
                    }
                });

                // Also show cancelled (removed) products in the OTHER/schedules section
                if (this.showChannelStatusDistinction()) {
                    (this.campaign.removedProducts || []).forEach(product => {
                        if (product.channels?.some(element => this.selectedChannels.has(element))) {
                            this.setSchedule(product, otherIdx, 'schedules');
                            otherIdx++;
                        }
                    });
                }

                const numberOfChannelsInOther = otherIdx != 0 ? 2 : 0;

                if (this.showChannelStatusDistinction()) this.campaign.products = resultProducts;

                if (
                    this.showChannelStatusDistinction() &&
                    this.campaign.channels.length > productChannels.length + numberOfChannelsInOther
                ) {
                    this.addSchedule();
                }

                this.addFormValuesToTheAccordionContent();
            }
        } else if (this.showChannelStatusDistinction()) {
            this.approvedSchedules.clear();
            this.addFormValuesToTheAccordionContent();
        }
    }

    getProductByChannelStatus(product, status) {
        const copiedProduct = { ...product };
        let filteredChannels = copiedProduct.channels.filter(channelId => {
            const channel = this.allAvailableChannels.find(
                channel => channel.idChannel === channelId
            );

            return status == 'APPROVED'
                ? approvedChannelStatuses.includes(channel?.status)
                : !approvedChannelStatuses.includes(channel?.status);
        });

        const filteredMedia = copiedProduct.media.filter(media => {
            if (status == 'APPROVED') {
                return (
                    media.status === 'APPROVED' ||
                    media.mediaChannels.some(mediaChannel =>
                        filteredChannels.includes(mediaChannel.channelId)
                    )
                );
            } else {
                return media.status !== 'APPROVED';
            }
        });

        if (filteredChannels.length != 0) {
            copiedProduct.channels = filteredChannels;
            copiedProduct.media = filteredMedia;
            return copiedProduct;
        } else {
            return null;
        }
    }

    onDateChanged(
        scheduleIndex: number,
        calculateImpressions: boolean = true,
        scheduleType: string = 'schedules'
    ) {
        const schedules = this.form.get(scheduleType) as FormArray;
        const schedule = schedules.at(scheduleIndex) as FormGroup;

        const productMatrix = schedule.get('productMatrix') as FormControl;
        productMatrix.patchValue([...[]]);

        const matrix = schedule.get('matrix') as FormControl;
        matrix.patchValue([...[]]);

        const startDate = moment((schedule.get('start_date') as FormControl).value);

        schedule.get('min_end_date').setValue(startDate);

        const endDate = moment((schedule.get('end_date') as FormControl).value);

        const datesDirective = schedule.get('datesDirective') as FormControl;

        if (this.startDateGreaterThanEndDate(schedule, true)) {
            datesDirective.setValue({ startDate: null, endDate: null });
        } else {
            datesDirective.setValue({ startDate, endDate });
        }

        if (calculateImpressions && !this.areSchedulesInvalid()) this.updateCampaign();
    }

    checkDatesExistenceInSchedule(schedule) {
        if (schedule.start_date && schedule.end_date) {
            return {
                startDate:
                    schedule.start_date instanceof moment
                        ? schedule.start_date
                        : moment(schedule.start_date),
                endDate:
                    schedule.end_date instanceof moment
                        ? schedule.end_date
                        : moment(schedule.end_date)
            };
        } else {
            return schedule.datesDirective;
        }
    }

    convertScheduleToProduct(schedule, scheduleIndex) {
        if (schedule.productMatrix) {
            let startDateMoment = this.checkDatesExistenceInSchedule(schedule).startDate;
            let endDateMoment = this.checkDatesExistenceInSchedule(schedule).endDate;

            let product = {
                channels: [...schedule.channels.map(channel => channel.idChannel)],
                startDate: startDateMoment.format('YYYY-MM-DD') + 'T00:00:00',
                endDate: endDateMoment.format('YYYY-MM-DD') + 'T00:00:00',
                dayPartings: [],
                media: []
            };

            let startDate = moment(this.checkDatesExistenceInSchedule(schedule).startDate);

            startDate = startDate.subtract(1, 'day');

            for (var i = 0; i < schedule.productMatrix.length; i++) {
                let date = startDate.add(1, 'day');
                let byteChar = '1010';
                for (var j = 0; j < schedule.productMatrix[i].length; j++) {
                    byteChar += '' + schedule.productMatrix[i][j];
                }
                product.dayPartings.push({
                    date: date.format('YYYY-MM-DD'),
                    slotHex: parseInt(byteChar, 2).toString(16).toUpperCase()
                });
            }

            if (
                this.campaign.products &&
                this.campaign.products[scheduleIndex] &&
                this.campaign.products[scheduleIndex].media
            ) {
                product.media = this.campaign.products[scheduleIndex].media;
            }

            return product;
        }
    }

    isScheduleDatesChannelsDirtied(schedule) {
        return (
            schedule.controls.start_date.dirty ||
            schedule.controls.end_date.dirty ||
            schedule.controls.channels.dirty
        );
    }

    areSchedulesInvalid() {
        const areAllChannelsSelected = !this.areAllChannelsSelected();
        const schedulesArray = this.form.get('schedules') as FormArray;

        for (let i = 0; i < schedulesArray.length; i++) {
            const schedule = schedulesArray.at(i) as FormGroup;

            const startDateValue = schedule.get('start_date').value;
            const endDateValue = schedule.get('end_date').value;

            if (!startDateValue || !endDateValue) return true;

            const start = moment(startDateValue);
            const end = moment(endDateValue);
            const min = moment(this.minStartDate).startOf('day');

            if (start.isBefore(min) || end.isBefore(min)) {
                return true;
            }

            if (!this.shouldScheduleAppear(schedule, 'schedules', i)) {
                return true;
            }

            const productMatrix = schedule.get('productMatrix')?.value;
            if (productMatrix) {
                const invalidHoursSlots = productMatrix.length == 0 || !productMatrix.some(arr => arr.includes(1));
                if (invalidHoursSlots) return true;
            }
        }

        if (this.campaign.budget < this.campaign.campaignTotalPrice?.value) {
            return true;
        }

        return areAllChannelsSelected;
    }
}
